import { DataObjectList, ObjectClassGenerator } from '../db/dataobject';
import { getMemoryTable } from '../db/datamanager';
import SampleSite, { SampleSiteList } from './SampleSiteClass';
import SamplingSpec, { SamplingSpecList } from './SamplingSpecClass';
import SoilCore, { SoilCoreList } from './SoilCoreClass';
import SampleBox, { SampleBoxList } from './SampleBoxClass';
import { flatten, orderBy } from 'lodash';
import { to_kml, to_feature, setBarcode, get_core_line_direction_deg, rotate_core_line } from '../db_ops/sample_ops';
import SamplingTypes from './SamplingTypesDatatype';
import { IKMLElement } from '../kml';
import Mission from './MissionClass';
import { BackupHistoryEntry, BackupSample, ISample, SampleBoxDeSerialized } from './types';
import { cleanSet, distanceLatLon, normalizeDoubleEncodedJsonString } from '../utils';
import { Coordinate } from 'ol/coordinate';
import SampleKmlLoader from '../services/mission-loading/original/from-kml/SampleKmlLoader';
import { areCoresClose, areCoresCoordinatesClose } from '../services/paths/utils';
import { undefinedSampleIdRegex } from '../constants';
import SampleCentroid from './SampleCentroidClass';
import { sampleCoresPullStatus } from '@rogoag/shared-ts';
import { dispatchSamplesUpdated, dispatchPathUpdated } from '../mapEvents';

export default class Sample extends ObjectClassGenerator<Sample>('Sample') implements ISample {
  // attributes
  #sample_id: string;
  #sample_type: SamplingTypes;
  #order: number;
  #previous_order: number;
  #bag_id?: string;
  // unfortunately everything relies on this really being the barcT extends DataObjectde scan time.
  // in an effort to remove that assocation, we will add two new attributes
  // - scanned_at
  // - sampled_at
  // for now, pulled_at will still be the scan time. But we may be able to change it to
  // be the actual sampled time if we can determine there won't be bad downstream effects.
  // because we don't actually have a good "core" sampled at time, we won't set that right now
  #sampled_at: number;
  #scanned_at: number;
  #pulled_at: number;
  #backup_history: string;

  // relationships
  #SampleSite_id?: number;
  #SamplingSpec_id?: number;
  #SampleBox_id?: number;

  static tableName = 'Sample';

  constructor(state: Partial<Sample> = {}) {
    super(state);
    // publish persistent attributes
    this.publishAttribute(Sample, 'sample_id');
    this.publishAttribute(Sample, 'sample_type');
    this.publishAttribute(Sample, 'order');
    this.publishAttribute(Sample, 'previous_order');
    this.publishAttribute(Sample, 'bag_id');
    this.publishAttribute(Sample, 'sampled_at');
    this.publishAttribute(Sample, 'scanned_at');
    this.publishAttribute(Sample, 'pulled_at');
    this.publishAttribute(Sample, 'SampleSite_id');
    this.publishAttribute(Sample, 'SamplingSpec_id');
    this.publishAttribute(Sample, 'SampleBox_id');
    this.publishAttribute(Sample, 'backup_history');
    // initialize state
    this.initializeState(state);
  }

  initializeState(state: Partial<Sample> = {}) {
    this._instance_id = state._instance_id!;
    this._refs = { ...state._refs };
    this._version = state._version!;
    this.#sample_id = state.sample_id || '';
    this.#sample_type = state.sample_type || 0;
    this.#order = state.order || 0;
    this.#previous_order = state.previous_order || 0;
    this.#bag_id = state.bag_id || '';
    this.#sampled_at = state.sampled_at || 0.0;
    this.#scanned_at = state.scanned_at || 0.0;
    this.#pulled_at = state.pulled_at || 0.0;
    this.#SampleSite_id = state.SampleSite_id;
    this.#SamplingSpec_id = state.SamplingSpec_id;
    this.#SampleBox_id = state.SampleBox_id;
    this.#backup_history = state.backup_history ?? '[]';
  }

  dispose() {
    const samplesite = this.getSampleSite();

    if (samplesite) {
      this.SampleSite_id = undefined;
    }

    const samplingspec = this.getSamplingSpec();

    if (samplingspec) {
      this.SamplingSpec_id = undefined;
    }

    for (const soilcore of this.getSoilCores()) {
      soilcore.Sample_id = undefined;
      soilcore.dispose();
    }

    const samplebox = this.getSampleBox();

    if (samplebox) {
      this.SampleBox_id = undefined;
    }

    Sample.delete(this.instance_id);
  }

  set sample_type(value) {
    this.#sample_type = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get sample_type() {
    return this.#sample_type;
  }

  set order(value) {
    this.#order = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get order() {
    return this.#order;
  }

  set previous_order(value) {
    this.#previous_order = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get previous_order() {
    return this.#previous_order;
  }

  set bag_id(value) {
    this.#bag_id = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get bag_id() {
    return this.#bag_id;
  }

  set sampled_at(value) {
    this.#sampled_at = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get sampled_at() {
    return this.#sampled_at;
  }

  set scanned_at(value) {
    this.#scanned_at = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get scanned_at() {
    return this.#scanned_at;
  }

  set pulled_at(value) {
    this.#pulled_at = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get pulled_at() {
    return this.#pulled_at;
  }

  set backup_history(value: string) {
    this.#backup_history = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get backup_history(): string {
    return this.#backup_history;
  }

  getSampleIdCombinedWithExternalRef() {
    let sampleId = this.sample_id;
    const externalRefId = this.getSampleSite()!.external_reference_id;

    if (externalRefId) {
      sampleId += `_${externalRefId}`;
    }

    return sampleId;
  }

  async replaceWithBackup(
    backupSample: BackupSample,
    context: { changeReason: string; mode: 'sampling' | 'maps'; userName: string },
  ) {
    const sampleSite = this.getSampleSite();
    const fromCentroid = this.getSampleCentroid();
    const fromInfo = {
      sample_id: this.sample_id,
      original_sample_id: sampleSite.original_sample_id ?? '',
      external_reference_id: sampleSite.external_reference_id ?? '',
      lat: fromCentroid?.lat || 0,
      lon: fromCentroid?.lon || 0,
    };

    await sampleSite.getSampleCentroid()?.move(backupSample.lat, backupSample.lon);
    this.sample_id = backupSample.sample_id;
    sampleSite.original_sample_id = backupSample.sample_id;
    sampleSite.external_reference_id = backupSample.ext_ref_id;

    this.resetPulled();

    const toInfo = {
      sample_id: backupSample.sample_id,
      original_sample_id: backupSample.sample_id,
      external_reference_id: backupSample.ext_ref_id,
      lat: backupSample.lat,
      lon: backupSample.lon,
    };

    const historyEntry: BackupHistoryEntry = {
      from: fromInfo,
      to: toInfo,
      change_reason: context.changeReason,
      change_timestamp: new Date().toISOString(),
      mode: context.mode,
      userName: context.userName,
    };

    const history: BackupHistoryEntry[] = JSON.parse(normalizeDoubleEncodedJsonString(this.#backup_history));
    history.push(historyEntry);

    this.backup_history = JSON.stringify(history);

    // Centroid moved + sample renamed → targeted rebuild on both old
    // and new IDs so CorePointsLayer/CentroidsLayer update.
    dispatchSamplesUpdated([fromInfo.sample_id, backupSample.sample_id]);
    // Centroid move shifts a waypoint → PathLayer needs to redraw.
    dispatchPathUpdated();
  }

  set change_reason(value) {
    let inst: SampleSite;

    if (Boolean((inst = this.getSampleSite()))) {
      inst.change_reason = value;
    }
  }

  get change_reason() {
    return this.getSampleSite()?.change_reason || '';
  }

  isSkippedWithTypeDelete() {
    return this.change_type === 'Delete';
  }

  get skipped_or_deleted() {
    return this.change_type === 'Delete' || this.change_type === 'Skip';
  }

  set change_type(value) {
    let inst: SampleSite;

    if (Boolean((inst = this.getSampleSite()))) {
      inst.change_type = value;
    }
  }

  get change_type() {
    return this.getSampleSite()?.change_type || 'None';
  }

  set sample_site_source(value) {
    let inst: SampleSite;

    if (Boolean((inst = this.getSampleSite()))) {
      inst.sample_site_source = value;
    }
  }

  get sample_site_source() {
    return this.getSampleSite()?.sample_site_source || 'Unknown';
  }

  get mission_name() {
    return this.getSampleSite()?.mission_name;
  }

  get last_modified() {
    return this.getSampleSite()?.last_modified;
  }

  set last_modified(value) {
    let inst: SampleSite;

    if (Boolean((inst = this.getSampleSite()))) {
      inst.last_modified = value;
    }
  }

  get sample_id() {
    return this.#sample_id;
  }

  set sample_id(value: string) {
    this.#sample_id = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get spec_id() {
    let inst: SamplingSpec | undefined;

    return Boolean((inst = this.getSamplingSpec())) ? inst!.spec_id : undefined;
  }

  get box_uid() {
    let inst: SampleBox | undefined;

    if (Boolean((inst = this.getSampleBox()))) {
      return inst?.uid;
    } else {
      return undefined;
    }
  }

  set SampleSite_id(value) {
    if (this.#SampleSite_id) {
      const relateObj = SampleSite.get(this.#SampleSite_id);

      if (relateObj) {
        relateObj.removeRelationshipData('Sample', this.instance_id);
      }
    }

    this.#SampleSite_id = value;

    if (value) {
      const relateObj = SampleSite.get(value);

      if (relateObj) {
        relateObj.addRelationshipData('Sample', this.instance_id);
      }
    }

    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get SampleSite_id() {
    return this.#SampleSite_id;
  }

  // @ts-ignore
  getSampleSite(): SampleSite {
    return SampleSite.get(this.SampleSite_id)!;
  }

  getSampleCentroid(): SampleCentroid | undefined {
    const sampleSite = this.getSampleSite();

    if (!sampleSite) {
      return undefined;
    }

    return sampleSite.getSampleCentroid();
  }

  set SamplingSpec_id(value) {
    if (this.#SamplingSpec_id) {
      const relateObj = SamplingSpec.get(this.#SamplingSpec_id);

      if (relateObj) {
        relateObj.removeRelationshipData('Sample', this.instance_id);
      }
    }

    this.#SamplingSpec_id = value;

    if (value) {
      const relateObj = SamplingSpec.get(value);

      if (relateObj) {
        relateObj.addRelationshipData('Sample', this.instance_id);
      }
    }

    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get SamplingSpec_id() {
    return this.#SamplingSpec_id;
  }

  getSamplingSpec() {
    return SamplingSpec.get(this.SamplingSpec_id);
  }

  async removeFromPath() {
    const mission = this.getSampleSite()?.getMission();

    if (!mission) {
      return;
    }

    this.#previous_order = this.#order;
    this.#order = -1;

    for (const wp of mission.getWaypoints()) {
      if (wp.getCorePoint()?.getSoilCore()?.Sample_id === this.instance_id) {
        wp.dispose();
      }
    }

    this.syncToDB();
  }

  getSoilCores() {
    if (this._refs?.SoilCore) {
      const soilCores: SoilCore[] = [];

      for (const id of this._refs.SoilCore) {
        const core: SoilCore | undefined = SoilCore.get(id);

        if (core) {
          soilCores.push(core);
        }
      }

      return new SoilCoreList(...soilCores);
    }

    const soilcores = SoilCore.query((sel) => sel && sel.Sample_id === this.instance_id);

    for (const soilcore of soilcores) {
      soilcore.Sample_id = this.instance_id; // re-set foreign key to force update of _refs
    }

    return new SoilCoreList(...soilcores);
  }

  isPending(): boolean {
    if (this.skipped_or_deleted) {
      return false;
    }

    if (this.hasBarcode()) {
      return false;
    }

    return this.hasNoCores() || this.getPendingSoilCores().length > 0;
  }

  getPulledSoilCores(): SoilCore[] {
    return this.getSoilCores().filter((sel) => sel.pulled);
  }

  getPendingSoilCores(): SoilCore[] {
    return this.getSoilCores().filter((sel) => sel.isPending());
  }

  hasCoresPulled(): boolean {
    return this.getPulledSoilCores().length > 0;
  }

  getExtraCores(): SoilCore[] {
    return this.getSoilCores().filter((s) => s.isExtraCore());
  }

  getCoreByLatLon(lat: number, lon: number): SoilCore | undefined {
    return this.getSoilCores().find((c) => areCoresCoordinatesClose(1, [lat, lon], [c.lat, c.lon]));
  }

  getNextSoilCoreTarget(robotOrArmPosition: Coordinate) {
    // order soil cores by waypoint order
    const cores = this.getSoilCores().toSorted((a, b) => a.core_number - b.core_number);

    let targetCore: SoilCore | undefined = undefined;

    const sampleHasPulledCores = this.hasCoresPulled();
    let firstCorePulled = false;

    // If a sample has at least one core that has already been pulled,
    // then our target is determined - it is a next core down the line.
    if (sampleHasPulledCores) {
      firstCorePulled = cores[0].pulled;

      // if the first core is not pulled, then we'll find the next available core starting from the back.
      if (!firstCorePulled) {
        cores.reverse();
      }

      targetCore = cores.find((core) => !core.pulled);
    }
    // If no cores have been pulled yet, then we allow coring from either end of the core line:
    // we choose the target edge core by the proximity to the current position of the robot.
    else {
      const edgeCores = this.getEdgeUnpulledSoilCores();

      if (edgeCores.length === 0) {
        return undefined;
      }

      if (edgeCores.length === 1) {
        return edgeCores[0];
      }

      // Get the closest edge core to the origin
      targetCore = edgeCores.reduce((prev, curr) => {
        const prevDistance = distanceLatLon(robotOrArmPosition[0], robotOrArmPosition[1], prev.lat, prev.lon, 'Feet');

        const currDistance = distanceLatLon(robotOrArmPosition[0], robotOrArmPosition[1], curr.lat, curr.lon, 'Feet');

        return prevDistance < currDistance ? prev : curr;
      });
    }

    return targetCore;
  }

  /** Cached by SoilCore.mutationCount — survives across get_instance
   *  calls because private fields aren't touched by initializeState. */
  #edgeUnpulledCache?: { result: SoilCore[]; mutations: number };

  getEdgeUnpulledSoilCores(): SoilCore[] {
    const table = getMemoryTable('SoilCore');

    if (this.#edgeUnpulledCache && this.#edgeUnpulledCache.mutations === (table?.mutationCount ?? 0)) {
      return this.#edgeUnpulledCache.result;
    }

    const soilcores = this.getSoilCores();
    let result: SoilCore[];

    if (soilcores.length === 0) {
      result = [];
    } else {
      soilcores.sort((a, b) => a.waypoint_index - b.waypoint_index);
      const unpulled = soilcores.filter((sel) => !sel.pulled);

      if (!unpulled.length) {
        result = [];
      } else if (unpulled.length === 1) {
        result = [unpulled[0]];
      } else {
        result = [unpulled[0], unpulled[unpulled.length - 1]];
      }
    }

    // Capture AFTER rebuild (lazy _refs may bump mutationCount)
    this.#edgeUnpulledCache = { result, mutations: table?.mutationCount ?? 0 };

    return result;
  }

  coresInfo(): { allCoresPulled: boolean; totalCoresNumber: number; pulledCoresNumber: number } {
    const totalSoilCores = this.getSoilCores() ?? [];
    const pulledSoilCores = totalSoilCores.filter((core) => core.pulled);

    return {
      allCoresPulled: pulledSoilCores.length >= totalSoilCores.length,
      totalCoresNumber: totalSoilCores.length,
      pulledCoresNumber: pulledSoilCores.length,
    };
  }

  disposeLastPulledCore() {
    const pulledCores = this.getPulledSoilCores();

    if (!pulledCores.length) {
      return;
    }

    const lastPulled = pulledCores
      .sort((a: SoilCore, b: SoilCore) => {
        return b.pulled_at - a.pulled_at; // for descending order
      })
      .at(0);

    if (!lastPulled) {
      return;
    }

    lastPulled.dispose();
  }

  hasPulledCoresReady(): {
    pulledCoresReady: boolean;
    totalCoresNumber: number;
    pulledCoresNumber: number;
    requiredPulledCoresNumber: number;
    plannedCoresNumber: number;
  } {
    const mission = this.getSampleSite()?.getMission();
    const spec = mission?.getPrimarySpec();

    if (!mission || !spec) {
      return {
        pulledCoresReady: false,
        totalCoresNumber: 0,
        pulledCoresNumber: 0,
        requiredPulledCoresNumber: 0,
        plannedCoresNumber: 0,
      };
    }

    const plannedCoresNumber = spec.cores;
    const { totalCoresNumber, pulledCoresNumber } = this.coresInfo();
    const { pulledCoresReady, requiredPulledCoresNumber } = sampleCoresPullStatus(
      mission.isBDMission(),
      plannedCoresNumber,
      pulledCoresNumber,
    );

    return {
      pulledCoresReady,
      totalCoresNumber,
      pulledCoresNumber,
      requiredPulledCoresNumber,
      plannedCoresNumber,
    };
  }

  set SampleBox_id(value) {
    if (this.#SampleBox_id) {
      const relateObj = SampleBox.get(this.#SampleBox_id);

      if (relateObj) {
        relateObj.removeRelationshipData('Sample', this.instance_id);
      }
    }

    this.#SampleBox_id = value;

    if (value) {
      const relateObj = SampleBox.get(value);

      if (relateObj) {
        relateObj.addRelationshipData('Sample', this.instance_id);
      }
    }

    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get SampleBox_id() {
    return this.#SampleBox_id;
  }

  getSampleBox() {
    return SampleBox.get(this.SampleBox_id);
  }

  async checkIntegrity() {
    const problems: string[] = [];
    // check uniqueness
    // check ID1
    const id1_duplicates = Sample.query(
      (sel: Sample) =>
        sel.instance_id !== this.instance_id &&
        sel.mission_name === this.mission_name &&
        sel.last_modified === this.last_modified &&
        sel.sample_id === this.sample_id &&
        sel.sample_type === this.sample_type,
    );

    for (const dup of id1_duplicates) {
      problems.push(`Duplicate sample found with ID1 for instance id ${this.instance_id}: ${dup.instance_id} (${dup})`);
    }

    // check relationships
    for (const tableName in this._refs) {
      Array.from(this._refs[tableName]).forEach((key) => {
        if (!getMemoryTable(tableName)?.getOne(key)) {
          problems.push(`sample: Could not find ${tableName} instance for ID: ${key}`);
        }
      });
    }

    if (!this.getSampleSite()) {
      problems.push(
        `sample: Could not find sample site instance across unconditional relationship R19: ${this.instance_id}`,
      );
    }

    if (!this.getSamplingSpec()) {
      problems.push(
        `sample: Could not find sampling spec instance across unconditional relationship R21: ${this.instance_id}`,
      );
    }

    return problems;
  }

  resetPulled() {
    this.getSoilCores().forEach((core) => core.resetPulled());

    // TODO delete rogo field ops cores?
  }

  to_kml({ use_pulled_locations = false } = {}) {
    return to_kml.bind(this)({ use_pulled_locations });
  }

  static async load_kml(point: IKMLElement, mission: Mission, deSerializeBoxes: SampleBoxDeSerialized[]) {
    const sampleKmlLoader = new SampleKmlLoader(mission, deSerializeBoxes);

    return await sampleKmlLoader.load(point);
  }

  calculatePulledCoresCentroid(): Coordinate {
    const pulledCores = this.getPulledSoilCores();

    if (!pulledCores.length) {
      return [0, 0];
    }

    return pulledCores
      .reduce(
        (accumulator, core) => {
          if (core.pulled_lat && core.pulled_lon) {
            accumulator[0] += core.pulled_lat;
            accumulator[1] += core.pulled_lon;
          }

          return accumulator;
        },
        [0, 0],
      )
      .map((sel) => sel / pulledCores.length);
  }

  to_feature(showBasedOnPulledCoreLocations: boolean) {
    return to_feature.bind(this)(showBasedOnPulledCoreLocations);
  }

  async setBarcode(barcode: string | undefined, boxInstanceId?: number) {
    await setBarcode.bind(this)(barcode, boxInstanceId);
  }

  /**
   * Direction this sample's core line is walked in (degrees, 0–360): the
   * bearing from its first core to its last, or null when undefined.
   */
  get_core_line_direction_deg(): number | null {
    return get_core_line_direction_deg.bind(this)();
  }

  /**
   * Rotate this sample's core line to be walked at `targetDirectionDeg` about
   * the sample centroid; a half turn swaps the line's ends. Refuses samples
   * with pulled cores.
   */
  async rotate_core_line(targetDirectionDeg: number): Promise<boolean> {
    return await rotate_core_line.bind(this)(targetDirectionDeg);
  }

  async clearBarcode() {
    if (this.hasBarcode()) {
      await this.setBarcode(undefined, this.SampleBox_id);
    }
  }

  hasBarcode() {
    return Boolean(this.bag_id);
  }

  getBarCode(): string | undefined {
    return this.bag_id;
  }

  getMission(): Mission | undefined {
    return this.getSampleSite()?.getMission();
  }

  getBarcodeRegex(): string {
    const spec = this.getSamplingSpec();

    if (!spec) {
      return '';
    }

    return spec.barcode_regex || '';
  }

  isUndefinedSample(): boolean {
    const match = this.sample_id.match(undefinedSampleIdRegex);

    return match !== null;
  }

  static extractNumberFromUndefinedSampleId(sampleId: string): number {
    const match = sampleId.match(undefinedSampleIdRegex);

    return match ? Number(match[1]) : 0;
  }

  static findMissionSampleBySampleID(mission: Mission, sampleID: string): Sample | undefined {
    return Sample.findOne((samp) => {
      if (!samp) {
        return false;
      }

      if (samp.sample_id !== sampleID) {
        return false;
      }

      const sampleMission = samp.getMission();

      if (!sampleMission) {
        return false;
      }

      return sampleMission.instance_id === mission.instance_id;
    });
  }

  static getSampleTypeByPoint(point: IKMLElement) {
    // Get the sample type from the parent folder
    const parent = point.findParent('Folder', (sel) => {
      const nameElement = sel?.find('name');

      if (!nameElement) {
        return false;
      }

      return ['ISNT 1', 'ISNT 2', 'Cyst'].includes(nameElement.getText());
    });

    if (!parent) {
      return SamplingTypes.REGULAR;
    }

    let samplingType = SamplingTypes.REGULAR;
    const name = parent.find('name')?.getText();

    if (name === 'ISNT 1') {
      samplingType = SamplingTypes.ISNT_1;
    } else if (name === 'ISNT 2') {
      samplingType = SamplingTypes.ISNT_2;
    } else if (name === 'Cyst') {
      samplingType = SamplingTypes.CYST;
    }

    return samplingType;
  }

  static createSample(
    mission: Mission,
    sampleSite: SampleSite,
    samplingSpec: SamplingSpec,
    sampleID: string,
    sampleType: SamplingTypes,
  ) {
    const sample = Sample.create();
    sample.SampleSite_id = sampleSite.instance_id;
    sample.SamplingSpec_id = samplingSpec.instance_id;
    sample.sample_id = sampleID;
    sample.sample_type = sampleType;

    sample.order =
      Math.max(
        ...cleanSet(flatten(mission.getSampleSites().map((site) => site.getSamples()))).map(
          (sel: { order: any }) => sel.order,
        ),
      ) + 1;

    return sample;
  }

  static distanceBetweenSamples(sampleA: Sample, sampleB: Sample): number {
    const centroidA = sampleA.getSampleCentroid();
    const centroidB = sampleB.getSampleCentroid();

    if (!centroidA || !centroidB) {
      // should not happen, just in case
      return Number.MAX_VALUE;
    }

    return distanceLatLon(centroidA.lat, centroidA.lon, centroidB.lat, centroidB.lon, 'Meters');
  }

  getOriginalSampleId(fallBackToSampleId: boolean = true): string {
    const sampleSite = this.getSampleSite();

    if (sampleSite) {
      return sampleSite.original_sample_id;
    }

    return fallBackToSampleId ? this.sample_id : '';
  }

  getMaxCoreNumber() {
    const orderedDesc = orderBy(this.getSoilCores(), ['core_number'], ['desc']);

    return orderedDesc.length > 0 ? orderedDesc[0].core_number : 0;
  }

  getNextAvailableCoreNumber(): number {
    return this.getMaxCoreNumber() + 1;
  }

  hasDuplicateCoresWithNumber(coreNumber: number): boolean {
    return this.getSoilCores().filter((core) => core.core_number === coreNumber).length > 1;
  }

  hasNoCores(): boolean {
    return this.getSoilCores().length === 0;
  }

  groupCoresByProximity(maxDistanceBetweenCloseCores: number) {
    const orderedCores = orderBy(this.getSoilCores(), ['core_number'], ['asc']);
    const out: SoilCore[][] = [];

    while (orderedCores.length > 0) {
      const group: SoilCore[] = [];
      const core = orderedCores.shift();

      if (!core) {
        break;
      }

      group.push(core);

      // Add all close cores to the group - irrespective of the order
      // We should not rely on core number or waypoint number ordering,
      // because sometimes those get messed up. So, we can't group by
      // making one walk of all cores unfortunately.
      let j = 0;

      while (j < orderedCores.length) {
        if (areCoresClose(maxDistanceBetweenCloseCores, core, orderedCores[j])) {
          group.push(orderedCores[j]);
          orderedCores.splice(j, 1);
        } else {
          ++j;
        }
      }

      out.push(group);
    }

    return out;
  }

  unskip() {
    if (this.change_type === 'Skip' || this.change_type === 'Delete') {
      this.change_type = 'None';
      this.change_reason = '';
    }
  }
}

export class SampleList extends DataObjectList<Sample> {
  getSampleSites() {
    return new SampleSiteList(...this.map((sample) => sample.getSampleSite()).filter((sel) => !!sel));
  }

  getSamplingSpecs() {
    return new SamplingSpecList(...this.map((sample) => sample.getSamplingSpec()).filter((sel) => !!sel));
  }

  getSoilCores() {
    return new SoilCoreList(
      ...this.reduce((soilCores: SoilCore[], sample) => {
        soilCores.push(...sample.getSoilCores());

        return soilCores;
      }, []).filter(Boolean),
    );
  }

  getSampleBoxes() {
    return new SampleBoxList(...this.map((sample) => sample.getSampleBox()).filter((sel) => !!sel));
  }
}
